import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getLeads, deleteLead } from '../services/firestore';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Search, 
  Users,
  Phone,
  Mail,
  Building2,
  MoreVertical,
  Pencil,
  Trash2,
  FileText
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatLeadToText } from '../services/leadFormatter';

export const LeadsPage = () => {
  const { userData, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, [userData]);

  useEffect(() => {
    filterLeads();
  }, [searchQuery, leads]);

  const fetchLeads = async () => {
    if (!userData) return;
    
    setLoading(true);
    try {
      const data = await getLeads(userData.uid, isAdmin());
      setLeads(data);
      setFilteredLeads(data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const filterLeads = () => {
    if (!searchQuery.trim()) {
      setFilteredLeads(leads);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = leads.filter(lead => 
      lead.contact_name?.toLowerCase().includes(query) ||
      lead.company_name?.toLowerCase().includes(query) ||
      lead.lead_no?.toLowerCase().includes(query) ||
      lead.phone?.includes(query)
    );
    setFilteredLeads(filtered);
  };

  const handleDelete = async () => {
    if (!leadToDelete) return;

    try {
      await deleteLead(leadToDelete.id);
      toast.success('Lead deleted successfully');
      fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Failed to delete lead');
    } finally {
      setDeleteDialogOpen(false);
      setLeadToDelete(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { label: 'New', className: 'badge-new' },
      contacted: { label: 'Contacted', className: 'badge-sent' },
      qualified: { label: 'Qualified', className: 'bg-purple-100 text-purple-800' },
      won: { label: 'Won', className: 'badge-won' },
      lost: { label: 'Lost', className: 'badge-lost' }
    };
    const config = statusConfig[status] || statusConfig.new;
    return <Badge className={`${config.className}`}>{config.label}</Badge>;
  };

  const LeadCard = ({ lead }) => (
    <Card className="data-card" data-testid={`lead-card-${lead.id}`}>
      <CardContent className="p-4">
       <div className="flex items-start justify-between">

  <div className="flex items-start gap-3 flex-1 min-w-0">
    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
      <Users className="w-5 h-5 text-blue-600" />
    </div>

    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-mono text-slate-500">{lead.lead_no}</span>
        {getStatusBadge(lead.status)}
      </div>

      <h3 className="font-semibold text-slate-900 mt-1 truncate">
        {lead.contact_name}
      </h3>

      {lead.company_name && (
        <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
          <Building2 size={14} />
          {lead.company_name}
        </p>
      )}
    </div>
  </div>

  {/* 🔥 DROPDOWN */}
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="icon" className="h-8 w-8">
        <MoreVertical size={16} />
      </Button>
    </DropdownMenuTrigger>

    <DropdownMenuContent align="end">

      <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}/edit`)}>
        <Pencil size={14} className="mr-2" />
        Edit
      </DropdownMenuItem>

      <DropdownMenuItem onClick={() => navigate(`/quotations/new?leadId=${lead.id}`)}>
        <FileText size={14} className="mr-2" />
        Create Quotation
      </DropdownMenuItem>

      <DropdownMenuItem
        onClick={() => {
          const text = formatLeadToText(lead);
          navigator.clipboard.writeText(text);
          toast.success("Lead copied");
        }}
      >
        📋 Copy Lead
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuItem
        className="text-red-600"
        onClick={() => {
          setLeadToDelete(lead);
          setDeleteDialogOpen(true);
        }}
      >
        <Trash2 size={14} className="mr-2" />
        Delete
      </DropdownMenuItem>

    </DropdownMenuContent>
  </DropdownMenu>

</div>

        <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-100">
          {lead.phone && (
            <a href={`tel:${lead.phone}`} className="flex items-center gap-1 text-sm text-slate-600 hover:text-amber-600">
              <Phone size={14} />
              {lead.phone}
            </a>
          )}
          {lead.email && (
            <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-sm text-slate-600 hover:text-amber-600 truncate">
              <Mail size={14} />
              {lead.email}
            </a>
          )}
        </div>

        {lead.source && (
          <div className="mt-2">
            <span className="text-xs text-slate-500">Source: {lead.source}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-12 w-full" />
        <div className="grid gap-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Leads</h1>
          <p className="text-slate-500 text-sm">{leads.length} leads</p>
        </div>

        <div className="flex gap-2">
          {/* NEW: Scan Button */}
          <Button 
            variant="outline"
            onClick={() => navigate('/leads/scan')}
          >
            Scan Card
          </Button>

          <Button 
            className="solar-gradient hidden md:flex"
            onClick={() => navigate('/leads/new')}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Search by name, company, or lead number..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-12"
        />
      </div>

      {/* Leads List */}
      {filteredLeads.length === 0 ? (
        <div className="empty-state py-12">
          <Users className="empty-state-icon" />
          <p className="text-slate-500 mb-2">
            {searchQuery ? 'No leads found matching your search' : 'No leads added yet'}
          </p>
          {!searchQuery && (
            <Button 
              className="solar-gradient mt-4"
              onClick={() => navigate('/leads/new')}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Lead
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredLeads.map(lead => (
            <LeadCard key={lead.id} lead={lead} />
          ))}
        </div>
      )}

      {/* FAB */}
      <button
        className="fab md:hidden"
        onClick={() => navigate('/leads/new')}
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete lead "{leadToDelete?.lead_no}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-500 hover:bg-red-600"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default LeadsPage;